<template>
  <h1>Delo</h1>

  <div class="row g-2 align-items-end mb-3">
    <div class="col-auto">
      <MonthPicker v-model="month" />
    </div>
  </div>

  <AbsenceModePicker v-model="mode" :can-edit="canEditAny" details range-hint=" v isti vrstici" />

  <div class="table-responsive">
    <table class="table table-sm table-bordered text-center absences-grid" @mouseleave="hoverWorker = hoverDay = null">
      <thead>
        <tr>
          <th rowspan="2" class="text-start name align-middle">Delavec</th>
          <th :colspan="days.length" class="small group" title="Plačane ure na dan ali odsotnost">ure</th>
          <th colspan="3" class="small group">dni</th>
          <th :colspan="TIME_SOURCES.length + 1" class="small group">ure</th>
        </tr>
        <tr>
          <th
            v-for="day in days"
            :key="day.key"
            :class="{ weekend: isWeekend(day.date), today: day.key === today, ...columnClass(day) }"
            class="small"
          >
            {{ day.date.date() }}<br />{{ day.date.format("dd") }}
          </th>
          <th class="small vacation" title="Dopust (dni)">D</th>
          <th class="small sick" title="Bolniška (dni)">B</th>
          <th class="small worked" title="Dni z vpisanim delom">W</th>
          <th v-for="s in PAID_SOURCES" :key="s.text" class="small" :title="s.title">{{ s.text }}</th>
          <th class="small total" title="Skupaj plačane ure">Σ</th>
          <th v-for="s in UNPAID_SOURCES" :key="s.text" class="small" :title="s.title">{{ s.text }}</th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="w in workers"
          :key="w.id"
          :class="{ 'hover-row': hoverWorker === w.id }"
          @mouseenter="hoverWorker = w.id"
        >
          <td class="text-start name text-nowrap">
            <router-link :to="{ name: 'WorkerEdit', params: { workerId: w.id } }"
              >{{ w.lastName }} {{ w.firstName }}</router-link
            >
          </td>
          <td
            v-for="day in days"
            :key="day.key"
            :class="cellClass(w, day)"
            :title="cellTitle(w, day)"
            @click="clickCell(w, day, $event)"
            @mouseenter="hoverDay = day.key"
            @mouseleave="hoverDay = null"
          >
            {{ dayText(w, day) }}
          </td>
          <td class="vacation">{{ count(w.id, "Vacation") || "" }}</td>
          <td class="sick">{{ count(w.id, "SickLeave") || "" }}</td>
          <td class="worked">{{ daysWorked(w.id) || "" }}</td>
          <td v-for="s in PAID_SOURCES" :key="s.text">{{ hours(sum(w.id, s)) || "" }}</td>
          <td class="total">{{ hours(paidSum(w.id)) || "" }}</td>
          <td v-for="s in UNPAID_SOURCES" :key="s.text">{{ hours(sum(w.id, s)) || "" }}</td>
        </tr>
        <tr v-if="!workers.length">
          <td :colspan="days.length + 5 + TIME_SOURCES.length" class="text-muted">Ni delavcev.</td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import { ref } from "vue"

// module level: the click mode survives the trip to a day view and back
const mode = ref("Details")
</script>

<script setup>
import { computed, watch, onMounted } from "vue"
import { useRoute, useRouter } from "vue-router"
import dayjs from "dayjs"
import { ABSENCE_MODES, datesBetween, isWeekend, toDay, useAbsences } from "../composition-api/useAbsences"
import AbsenceModePicker from "../components/AbsenceModePicker.vue"
import MonthPicker from "../components/MonthPicker.vue"

defineOptions({ name: "Absences" })

// #990 columns of WorkerDailyTimes; monthly columns sum the minutes and round at the end
const TIME_SOURCES = [
  { key: "serviceMinutes", text: "SDN", title: "Servis (ure)", paid: true },
  { key: "productionMinutes", text: "PDN", title: "Proizvodnja (ure)", paid: true },
  { key: "internalMinutes", text: "Int.", title: "Interni nalogi (ure)", paid: true },
  { key: "paidTravelMinutes", text: "Pot", title: "Pot, voznik in sopotnik (ure)", paid: true },
  { key: "unpaidTravelMinutes", text: "Na/iz", title: "Prihod/odhod iz službe (ure)", paid: false },
]
// paid columns add up to Σ; unpaid ones come after it
const PAID_SOURCES = TIME_SOURCES.filter((s) => s.paid)
const UNPAID_SOURCES = TIME_SOURCES.filter((s) => !s.paid)
const sourceMinutes = (t, s) => t?.[s.key] ?? 0

const { loadAbsencesAsync, setAbsencesAsync } = useAbsences()

const route = useRoute()
const router = useRouter()
// month is kept in the URL, so coming back from a day view returns to it
const month = ref((route.query.month ? dayjs(route.query.month) : dayjs()).startOf("month"))
const workers = ref([])
const absences = ref({}) // { workerId: { "YYYY-MM-DD": type } }
const times = ref({}) // #990 { workerId: { "YYYY-MM-DD": { serviceMinutes, ... } } }, recalculated nightly
const lockDate = ref(null)
const anchor = ref(null) // { workerId, date } of the last click, start of a shift-click range
const hoverWorker = ref(null) // row and column under the mouse, highlighted as a crosshair
const hoverDay = ref(null)
const canEditAny = computed(() => workers.value.some((w) => w.canEdit))

// times are recalculated nightly, so they cover days up to yesterday
const today = dayjs().format("YYYY-MM-DD")
const yesterday = dayjs().subtract(1, "day").format("YYYY-MM-DD")
const columnClass = (day) => ({
  "data-end": day.key === yesterday,
  "today-end": day.key === today,
  "hover-col": day.key === hoverDay.value,
})

const days = computed(() => {
  const result = []
  for (let d = month.value; d.month() === month.value.month(); d = d.add(1, "day")) {
    result.push({ date: d, key: d.format("YYYY-MM-DD") })
  }
  return result
})

const typeText = (type) => ABSENCE_MODES.find((m) => m.value === type)?.text ?? type
const typeShort = (type) => (type ? (ABSENCE_MODES.find((m) => m.value === type)?.short ?? "?") : "")

const isLocked = (w, date) => !w.canEdit || (lockDate.value && date.isBefore(lockDate.value, "day"))

const cellClass = (w, day) => ({
  ...columnClass(day),
  weekend: isWeekend(day.date),
  vacation: absences.value[w.id]?.[day.key] === "Vacation",
  sick: absences.value[w.id]?.[day.key] === "SickLeave",
  // officially absent, but time was logged anyway
  conflict: !!absences.value[w.id]?.[day.key] && paidMinutes(times.value[w.id]?.[day.key]) > 0,
  editable: mode.value === "Details" || !isLocked(w, day.date),
})

const count = (workerId, type) => Object.values(absences.value[workerId] ?? {}).filter((t) => t === type).length

// day cell shows paid time; unpaid travel (commute) only in its own column
const paidMinutes = (t) => PAID_SOURCES.reduce((total, s) => total + sourceMinutes(t, s), 0)
const hours = (minutes) => Math.round(minutes / 60)
const sum = (workerId, s) =>
  Object.values(times.value[workerId] ?? {}).reduce((total, t) => total + sourceMinutes(t, s), 0)
const paidSum = (workerId) => Object.values(times.value[workerId] ?? {}).reduce((total, t) => total + paidMinutes(t), 0)
const daysWorked = (workerId) => Object.values(times.value[workerId] ?? {}).filter((t) => paidMinutes(t) > 0).length

const dayText = (w, day) => {
  const minutes = paidMinutes(times.value[w.id]?.[day.key])
  return hours(minutes) || typeShort(absences.value[w.id]?.[day.key])
}

const cellTitle = (w, day) => {
  const type = absences.value[w.id]?.[day.key]
  const t = times.value[w.id]?.[day.key]
  const lines = type ? [typeText(type)] : []
  if (t) {
    for (const s of TIME_SOURCES) {
      const m = sourceMinutes(t, s)
      if (m) lines.push(`${s.title}: ${Math.floor(m / 60)}:${String(m % 60).padStart(2, "0")}`)
    }
  }
  return lines.join("\n")
}

const loadAsync = async () => {
  const raw = await loadAbsencesAsync({
    from: month.value,
    to: month.value.endOf("month"),
  })
  if (!raw) return
  lockDate.value = toDay(raw.lockDate)
  workers.value = raw.workers ?? []
  const map = {}
  for (const a of raw.result) {
    if (!map[a.workerId]) map[a.workerId] = {}
    map[a.workerId][a.date] = a.type
  }
  absences.value = map
  const timeMap = {}
  for (const t of raw.times ?? []) {
    if (!timeMap[t.workerId]) timeMap[t.workerId] = {}
    timeMap[t.workerId][t.date] = t
  }
  times.value = timeMap
  anchor.value = null
  // without edit rights the click can only open the day
  if (!canEditAny.value) mode.value = "Details"
}

const clickCell = async (w, day, event) => {
  if (mode.value === "Details") {
    router.push({ name: "AbsenceDay", params: { workerId: w.id, date: day.key } })
    return
  }
  if (isLocked(w, day.date)) return
  const range =
    event.shiftKey && anchor.value?.workerId === w.id ? datesBetween(anchor.value.date, day.date) : [day.date]
  anchor.value = { workerId: w.id, date: day.date }
  const current = absences.value[w.id] ?? {}
  const wanted = mode.value === "Present" ? undefined : mode.value
  const dates = range.filter((d) => !isLocked(w, d) && current[d.format("YYYY-MM-DD")] !== wanted)
  if (!dates.length) return

  const raw = await setAbsencesAsync(w.id, dates, mode.value)
  if (!raw) return
  const updated = { ...current }
  for (const d of dates) {
    if (wanted) updated[d.format("YYYY-MM-DD")] = wanted
    else delete updated[d.format("YYYY-MM-DD")]
  }
  absences.value = { ...absences.value, [w.id]: updated }
}

watch(month, () => {
  router.replace({ query: { ...route.query, month: month.value.format("YYYY-MM") } })
  loadAsync()
})
onMounted(loadAsync)
</script>

<style scoped>
.absences-grid {
  font-size: 0.8rem;
}
.absences-grid th,
.absences-grid td {
  padding: 0.15rem 0.1rem;
  min-width: 1.5rem;
  user-select: none;
}
.absences-grid .name {
  position: sticky;
  left: 0;
  z-index: 1;
  background: var(--bs-body-bg);
  min-width: 8rem;
}
.absences-grid .group {
  font-weight: normal;
  color: var(--bs-secondary-color);
}
.absences-grid .weekend {
  background: var(--bs-tertiary-bg);
}
.absences-grid .worked,
.absences-grid .total {
  background: var(--bs-success-bg-subtle);
  font-weight: bold;
}
.absences-grid .vacation {
  background: var(--bs-primary-bg-subtle);
  font-weight: bold;
}
.absences-grid .sick {
  background: var(--bs-warning-bg-subtle);
  font-weight: bold;
}
.absences-grid td.conflict {
  color: var(--bs-danger);
  box-shadow: inset 0 0 0 2px var(--bs-danger);
}
.absences-grid th.today {
  color: var(--bs-primary);
}
/* last day with aggregated times | today | from tomorrow on */
.absences-grid .data-end {
  border-right: 2px dashed var(--bs-secondary-color);
}
.absences-grid .today-end {
  border-right: 2px solid var(--bs-primary);
}
/* overlay keeps the cell's own background (weekend, vacation, ...) visible */
.absences-grid .hover-col,
.absences-grid tr.hover-row > td {
  background-image: linear-gradient(rgba(var(--bs-primary-rgb), 0.12), rgba(var(--bs-primary-rgb), 0.12));
}
.absences-grid td.editable {
  cursor: pointer;
}
.absences-grid td.editable:hover {
  outline: 2px solid var(--bs-primary);
  outline-offset: -2px;
}
</style>
